import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Col, Descriptions, Row, Table, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  EditOutlined,
  FileExcelOutlined,
  HistoryOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import fileDownload from 'js-file-download';
import moment from 'moment';

import styles from '../styles';
import {
  getFabricInventory,
  clearFabricInventory,
  getFabricTransferHistory,
  clearFabricTransferHistory,
  getFabricInventoryStats,
  clearFabricInventoryStats,
} from '../Actions';
import { sorter, onFilter, filterDropdown, filterIcon, get } from '../utils';
import Breadcrumbs from '../Components/Breadcrumbs';
import constants from '../constants';
import TransferHistory from './TransferHistory';
import Api from '../Api';
import { errorNotificationOnBlob } from '../Snackbar';

const InventoryList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  const fabricInventory = useSelector(state => state.fabricInventory);
  const fabricInventoryStats = useSelector(state => state.fabricInventoryStats);
  const fabricTransferHistory = useSelector(
    state => state.fabricTransferHistory
  );

  const [recordId, setRecordId] = useState(null);
  const [transferHistoryModal, setTransferHistoryModal] = useState(false);

  useEffect(() => {
    dispatch(getFabricInventory());
    dispatch(getFabricInventoryStats());
    return () => {
      dispatch(clearFabricInventory());
      dispatch(clearFabricInventoryStats());
    };
  }, []);

  useEffect(() => {
    if (recordId && transferHistoryModal) {
      dispatch(getFabricTransferHistory({ fabricInventoryId: recordId }));
    }
  }, [recordId, transferHistoryModal]);

  const exportExcelFile = useCallback(async () => {
    try {
      const { data } = await Api.getFile('/fabric-inventory/export');
      fileDownload(
        data,
        `Fabric-Inventory-${moment().format('DD MMM YYYY')}.csv`
      );
    } catch (error) {
      errorNotificationOnBlob(error);
    }
  }, []);

  const exportGroupedExcelFile = useCallback(async () => {
    try {
      const { data } = await Api.getFile('/fabric-inventory/export/grouped');
      fileDownload(
        data,
        `Fabric-Inventory-Grouped-${moment().format('DD MMM YYYY')}.csv`
      );
    } catch (error) {
      errorNotificationOnBlob(error);
    }
  }, []);

  const showModal = useCallback(id => {
    setRecordId(id);
    setTransferHistoryModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setRecordId(null);
    setTransferHistoryModal(false);
    dispatch(clearFabricTransferHistory());
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.modifyFabricInventory) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/inventory/fabric/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const transferColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.modifyFabricInventory) ? (
            <Tooltip placement="bottom" title="Transfer">
              <Link to={`/inventory/fabric/transfer/${record.id}`}>
                <RetweetOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const transferHistoryColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(
            constants.permissions.fabricInventoryTransferAuthorization
          ) && record.transferCount > 0 ? (
            <Tooltip placement="bottom" title="History">
              <HistoryOutlined
                className={classes.tableIcon}
                onClick={() => showModal(record.id)}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const _columns = [
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
        sorter: sorter('fabric'),
        onFilter: onFilter('fabric'),
        filterDropdown,
        filterIcon,
        width: '15%',
      },
      {
        title: 'Width',
        dataIndex: 'width',
        sorter: sorter('width'),
        onFilter: onFilter('width'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
            sorter: sorter('baseColor'),
            onFilter: onFilter('baseColor'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
            sorter: sorter('printColor'),
            onFilter: onFilter('printColor'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Factory',
        dataIndex: 'factory',
        sorter: sorter('factory'),
        onFilter: onFilter('factory'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Rack #',
        dataIndex: 'rackno',
        sorter: sorter('rackno'),
        onFilter: onFilter('rackno'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.modifyFabricInventory)) {
      _columns.push(
        {
          title: 'Update',
          render: updateColumn,
        },
        {
          title: 'Transfer',
          render: transferColumn,
        }
      );
    }

    if (
      permissions.includes(
        constants.permissions.fabricInventoryTransferAuthorization
      )
    ) {
      _columns.push({
        title: 'History',
        render: transferHistoryColumn,
      });
    }

    return _columns;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1800, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Fabric Inventory"
        moduleLink="/inventory/fabric"
        subModuleName=""
      />
      <Row className={classes.paddingToContainerContent}>
        <Col className={classes.marginBetweenSelect}>
          <div>Export</div>
          <Button icon={<FileExcelOutlined />} onClick={exportExcelFile}>
            Export
          </Button>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Export</div>
          <Button icon={<FileExcelOutlined />} onClick={exportGroupedExcelFile}>
            Grouped
          </Button>
        </Col>
      </Row>
      <div style={{ padding: 10 }}>
        <Descriptions
          bordered
          size="small"
          labelStyle={{ fontWeight: 500 }}
          column={{ xxl: 8, xl: 6, lg: 4, md: 3, sm: 2, xs: 1 }}
        >
          {fabricInventoryStats.map(stats => (
            <Descriptions.Item key={stats.factory} label={stats.factory}>
              {stats.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={fabricInventory}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
      {transferHistoryModal ? (
        <TransferHistory
          hideModal={hideModal}
          transferHistoryModal={transferHistoryModal}
          fabricTransferHistory={fabricTransferHistory}
        />
      ) : null}
    </>
  );
};

InventoryList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(InventoryList);
